import { useCallback, useState } from "react";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { CargoTabela } from "../../domain/models/cargo_tabela";
import { useNavigate } from "react-router";
import { initialFiltersValues, useCargosListaFilters } from "../hooks/Cargos_lista/filters"; //
import { useCargosListaPagination } from "../hooks/Cargos_lista/paginations"; //
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

/**
 * LoadCargosFiltersState
 */

type LoadCargosFiltersState = {
  nome?: string;
  atribuicoes?: string;
  escolaridade?: string;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useCargosListaViewModel
 */

export const useCargosListaViewModel = () => {
  const {
    reports: { getReportCargosUseCase },
    rh: { getAllCargoUseCase, deleteCargoUseCase, copyCargoUseCase },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [selectedCargo, setSelectedCargo] = useState<CargoTabela>();

  const [cargos, setCargos] = useState<CargoTabela[]>();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filters = useCargosListaFilters();
  const pagination = useCargosListaPagination();

  const navigate = useNavigate();

  /**
   * Funções de carregamento
   */

  const loadCargos = useCallback(
    async (filtersState?: LoadCargosFiltersState) => {
      try {
        const filteredNome = filtersState?.nome ?? filters.nome;
        const filteredAtribuicoes = filtersState?.atribuicoes ?? filters.atribuicoes;
        const filteredEscolaridade = filtersState?.escolaridade ?? filters.escolaridade;
        const filteredStatus = filtersState?.status ?? filters.status;

        const filteredPage = filtersState?.page ?? pagination.page;
        const filteredRowsPerPage = filtersState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllCargoUseCase.getAllCargo(
          filteredNome,
          filteredAtribuicoes,
          filteredEscolaridade,
          filteredStatus,
          filteredPage,
          filteredRowsPerPage,
        );

        setCargos(getAllCargoUseCase.items);

        pagination.setTotalItems(getAllCargoUseCase.totalItems);
      } catch (e: unknown) {
        pagination.setTotalItems(0);
        setCargos([]);
        throw e;
      }
    },
    [filters, pagination, getAllCargoUseCase],
  );

  /**
   * UseEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCargos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCargos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
    } finally {
      setLoading(false);
    }
  }, [loadCargos, pushErrorNotification]);

  const handleRelatorio = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (mimeType) => await getReportCargosUseCase.getReportCargos(mimeType),
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório de cargos!"));
    } finally {
      setLoading(false);
    }
  }, [getReportCargosUseCase, startReportViewerProcess, pushErrorNotification]);

  const handleNewCargo = useCallback(
    (openNewTab: boolean) => {
      if (openNewTab) {
        window.open(`/Cargos_cadastro`, "_blank", "noopener,noreferrer");
      } else {
        navigate("/Cargos_cadastro");
      }
    },
    [navigate],
  );

  const handleNomeChange = useDebouncedCallback(
    debouncedRef,
    (nome: string) => {
      useCargosListaFilters.setState({ nome });
      return async () => {
        try {
          setLoading(true);
          useCargosListaPagination.setState({ page: 1 });
          await loadCargos({ nome, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadCargos, pushErrorNotification],
  );

  const handleAtribuicoesChange = useDebouncedCallback(
    debouncedRef,
    (atribuicoes: string) => {
      useCargosListaFilters.setState({ atribuicoes });
      return async () => {
        try {
          setLoading(true);
          useCargosListaPagination.setState({ page: 1 });
          await loadCargos({ atribuicoes, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadCargos, pushErrorNotification],
  );

  const handleEscolaridadeChange = useDebouncedCallback(
    debouncedRef,
    (escolaridade: string) => {
      useCargosListaFilters.setState({ escolaridade });
      return async () => {
        try {
          setLoading(true);
          useCargosListaPagination.setState({ page: 1 });
          pagination.setPage(1);
          await loadCargos({ escolaridade, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, pagination, loadCargos, pushErrorNotification],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    (status: number) => {
      useCargosListaFilters.setState({ status });
      return async () => {
        try {
          setLoading(true);
          useCargosListaPagination.setState({ page: 1 });
          await loadCargos({ status, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadCargos, pushErrorNotification],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useCargosListaFilters.getState().clearFilters();
      useCargosListaPagination.setState({ page: 1 });
      await loadCargos({ ...initialFiltersValues, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
    } finally {
      setLoading(false);
    }
  }, [loadCargos, pushErrorNotification]);

  const handleCargoCadastroOpenState = useCallback(
    (idCargo: number | null, openNewTab: boolean) => {
      if (openNewTab) {
        window.open(`/Cargos_cadastro?id=${idCargo}`, "_blank", "noopener,noreferrer");
      } else {
        navigate(`/Cargos_cadastro?id=${idCargo}`);
      }
    },
    [navigate],
  );

  const handleCargoDeleteOpenState = useCallback(
    (idCargo: number | null, openState: boolean) => {
      if (openState) {
        if (idCargo && cargos) {
          setSelectedCargo(cargos.find((x) => x.idCargo === idCargo));
        } else {
          setSelectedCargo(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [cargos],
  );

  const handleConfirmarDeletarCargo = useCallback(async () => {
    try {
      if (selectedCargo) {
        setSaving(true);

        await deleteCargoUseCase.deleteCargo(selectedCargo.idCargo);
        await loadCargos();
        pushInfoNotification("Sucesso", "Registro excluido com sucesso!");
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao deletar cargo!"));
    } finally {
      setSaving(false);
      setOpenDeleteModal(false);
    }
  }, [selectedCargo, deleteCargoUseCase, loadCargos, pushErrorNotification, pushInfoNotification]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        pagination.setPage(page + 1);
        await loadCargos({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
      } finally {
        setLoading(false);
      }
    },
    [pagination, loadCargos, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadCargos({ rowsPerPage, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar cargos!"));
      } finally {
        setLoading(false);
      }
    },
    [pagination, loadCargos, pushErrorNotification],
  );

  const handleCargoExportar = useCallback(
    async (idCargo: number) => {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (mimeType) => await getReportCargosUseCase.getReportCargos(mimeType, idCargo),
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório de cargos!"));
      } finally {
        setLoading(false);
      }
    },
    [getReportCargosUseCase, startReportViewerProcess, pushErrorNotification],
  );

  const handleCargoCopyOpenState = useCallback(
    async (idCargo: number | null, openState: boolean) => {
      if (openState) {
        if (idCargo && cargos) {
          setSelectedCargo(cargos.find((x) => x.idCargo === idCargo));
        } else {
          setSelectedCargo(undefined);
        }
      }
      setOpenCopyModal(openState);
    },
    [cargos],
  );

  const handleCargoCopy = useCallback(
    async (descricao: string) => {
      try {
        if (!loading && !saving && selectedCargo) {
          setLoading(true);
          setSaving(true);

          await copyCargoUseCase.copyCargo(selectedCargo.idCargo, descricao);
          await loadCargos();
          pushInfoNotification("Sucesso", "Registro copiado com sucesso!");
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório de cargos!"));
      } finally {
        setLoading(false);
        setSaving(false);
        setOpenCopyModal(false);
      }
    },
    [loading, saving, selectedCargo, copyCargoUseCase, loadCargos, pushErrorNotification, pushInfoNotification],
  );

  return {
    loading,
    saving,
    openDeleteModal,
    openCopyModal,
    errorMessage,
    selectedCargo,
    cargos: cargos ?? [],
    reportViewerState,
    filters,
    pagination,
    handleReload,
    handleRelatorio,
    handleNewCargo,
    handleNomeChange,
    handleAtribuicoesChange,
    handleEscolaridadeChange,
    handleStatusChange,
    handleClearFilters,
    handleCargoCadastroOpenState,
    handleCargoDeleteOpenState,
    handleConfirmarDeletarCargo,
    handlePageChange,
    handleRowsPerPageChange,
    handleCargoExportar,
    handleCargoCopyOpenState,
    handleCargoCopy,
  };
};
